import { Injectable } from '@angular/core';
import { catchError, defaultIfEmpty, filter, map, shareReplay, switchMap, take, tap, timeout } from 'rxjs/operators';
import { combineLatest, firstValueFrom, from, Observable, of, throwError } from 'rxjs';

import { NativeStorageService } from '../data/native-storage.service';
import { SettingsService } from '../settings/service/settings.service';
import { ArmyBuilderConfig } from '../config';
import { HttpClientRequestOptions, HttpClientWithInFlightCache } from '../httpClient';

const CACHE_TIMEOUT = 1000 * 60 * 60 * 24;

export interface HttpRequestConfig {
    cacheKey?: string;
    requiresLogin?: boolean;
    preferCache?: boolean;
    writeToCache?: boolean;
    fallbackToCache?: boolean;
    mergeWithCache?: boolean;
}
@Injectable({ providedIn: 'root' })
export class RestDataService {
    serverCacheTime$: Observable<Date>;
    localCacheTime$: Observable<Date>;
    cacheUrl: string;
    constructor(
        private httpClient: HttpClientWithInFlightCache,
        private storage: NativeStorageService,
        private settingsService: SettingsService,
        private config: ArmyBuilderConfig
    ) {
        this.cacheUrl = this.config.apiBaseUrl + '/library/cacheTime';
        this.serverCacheTime$ = this.httpClient.get(this.cacheUrl).pipe(
            shareReplay(1),
            catchError(() => of(new Date(0)))
        );
    }

    get(url: string, config: HttpRequestConfig) {
        const combinedConfig: HttpRequestConfig = {
            requiresLogin: false,
            preferCache: true,
            writeToCache: true,
            fallbackToCache: true,
            mergeWithCache: false,
            ...config
        };
        const opts: HttpClientRequestOptions = {
            headers: {}
        };

        let { cacheKey, requiresLogin, preferCache, writeToCache: useCache, mergeWithCache, fallbackToCache } = combinedConfig;

        if (requiresLogin) {
            opts.withCredentials = true;
            opts.requiresLogin = true;
        }

        return this.settingsService.loggedIn$.pipe(
            filter((loggedIn) => {
                return !requiresLogin || loggedIn;
            }),
            switchMap(() => {
                return from(
                    this.storage.getItem(cacheKey, undefined).then(async (cached) => {
                        const urlParams = new URLSearchParams(location.search);
                        if (urlParams?.get('clearCache')) {
                            preferCache = false;
                        }

                        console.log('Fetching data from server: ', cacheKey, url);
                        let cachedItems = [];
                        const baseUrl = url;

                        if (mergeWithCache) {
                            cachedItems = cached?.data?.items || [];
                            // Optimize URL building using URLSearchParams for better performance
                            const urlObj = new URL(url, location.origin);

                            // Further optimize localCacheTime calculation - avoid Date objects entirely
                            let localCacheTime = 0;
                            for (let i = 0; i < cachedItems.length; i++) {
                                const item = cachedItems[i];
                                const itemTime = item?.lastUpdated || 0;
                                // If lastUpdated is already a timestamp, use it directly; otherwise parse it
                                const timeValue = typeof itemTime === 'number' ? itemTime : Date.parse(itemTime);
                                if (timeValue > localCacheTime) {
                                    localCacheTime = timeValue;
                                }
                            }
                            urlObj.searchParams.set('since', localCacheTime.toString());
                            url = urlObj.toString();
                        }

                        try {
                            let data = await firstValueFrom(this.httpClient.get(url, opts).pipe(timeout(30000)));
                            let res = data;

                            if (mergeWithCache) {
                                console.log(`Merging ${data.items?.length} items for ${cacheKey}`);

                                // Early return if no new items to merge
                                if (!data.items?.length) {
                                    // Detect deleted entities: server itemCount (total in DB) is less
                                    // than our cached count, meaning entities have been deleted.
                                    if (data.itemCount > 0 && cachedItems.length > data.itemCount) {
                                        console.log(
                                            `Stale cache detected for ${cacheKey}: server has ${data.itemCount} items, cache has ${cachedItems.length}. Re-fetching full dataset.`
                                        );
                                        data = await firstValueFrom(this.httpClient.get(baseUrl, opts).pipe(timeout(30000)));
                                        res = data;
                                    } else {
                                        res = {
                                            ...res,
                                            itemCount: cachedItems.length,
                                            items: cachedItems
                                        };
                                    }
                                } else {
                                    const itemMap = new Map(cachedItems.map((item) => [item._id, item]));

                                    // Batch update items for better performance
                                    for (let i = 0; i < data.items.length; i++) {
                                        const item = data.items[i];
                                        itemMap.set(item._id, item);
                                    }

                                    // Convert back to array
                                    const mergedItems = Array.from(itemMap.values());

                                    // Detect deleted entities: server itemCount (total in DB) is less
                                    // than our merged count, meaning entities have been deleted.
                                    if (data.itemCount > 0 && mergedItems.length > data.itemCount) {
                                        console.log(
                                            `Stale cache detected for ${cacheKey}: server has ${data.itemCount} items, merged has ${mergedItems.length}. Re-fetching full dataset.`
                                        );
                                        data = await firstValueFrom(this.httpClient.get(baseUrl, opts).pipe(timeout(30000)));
                                        res = data;
                                    } else {
                                        res = {
                                            ...res,
                                            itemCount: mergedItems.length,
                                            items: mergedItems
                                        };
                                    }
                                }
                            }

                            if (useCache) {
                                console.log(`Caching ${res?.items?.length} / ${res?.itemCount} items for ${cacheKey}`);
                                this.storage.setItem(cacheKey, { timestamp: Date.now(), data: res });
                            }

                            return res;
                        } catch (err) {
                            if (err.status === 401) {
                                console.error('User is no longer logged in');
                                return;
                            }
                            console.groupCollapsed('RestDataService: Error retrieving ' + cacheKey + ', falling back to cache.', err);
                            console.error('RestDataService: Error retrieving ' + cacheKey + ', falling back to cache.', err);
                            console.trace('RestDataService: Error stack trace');
                            console.groupEnd();

                            if (fallbackToCache && cached?.data) {
                                return cached.data;
                            }
                            throw err;
                        }
                    })
                );
            }),
            take(1)
        );
    }
}
